import { SvgIconComponent } from '@mui/icons-material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { FC, useState } from 'react';
import { useTranslations } from 'use-intl';

import LoadingButton from '../LoadingButton/LoadingButton';
import styles from './IntegrationCard.module.scss';
import DeleteDialog from '../DeleteDialog/DeleteDialog';

interface IntegrationCardProps {
  icon: SvgIconComponent;
  name: string;
  description: string | { label: string; value: string }[];
  loading: boolean;
  error: string | null;
  onIntegrate: () => void;
  onConfigure?: () => void;
  onDelete?: () => Promise<void>;
  isIntegrated: boolean;
}

const IntegrationCard: FC<IntegrationCardProps> = (props) => {
  const {
    icon: SocialMediaIcon,
    name,
    loading,
    error,
    isIntegrated,
    description,
    onIntegrate,
    onConfigure,
    onDelete,
  } = props;

  const t = useTranslations('fields');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  return (
    <Card id={styles.integrationCard}>
      <CardContent className={styles.content}>
        <Box className={styles.iconContainer}>
          <SocialMediaIcon />
        </Box>
        <Box className={styles.info}>
          <Box className={styles.titleContainer}>
            <Box className={styles.text}>
              <Typography
                variant='h6'
                // color='text.secondary'
                className={styles.title}
              >
                {name}
              </Typography>
              {isIntegrated && !loading && (
                <CheckCircleRoundedIcon fontSize='small' color='success' />
              )}
            </Box>
            {isIntegrated && onDelete && (
              <IconButton
                onClick={() => setShowDeleteDialog(true)}
                color='error'
              >
                <LogoutIcon />
              </IconButton>
            )}
          </Box>
          <Box className={styles.descriptionContainer}>
            {typeof description === 'string' ? (
              <Typography variant='body1' color='secondary'>
                {description}
              </Typography>
            ) : (
              description.map(({ label, value }) => (
                <Box key={label} className={styles.description}>
                  <Typography variant='body2' color='secondary'>
                    {label}
                  </Typography>
                  <Typography variant='body2' fontWeight={500}>
                    {value}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
        <Box className={styles.actions}>
          {(!isIntegrated || loading) && (
            <LoadingButton
              loading={loading}
              disabled={!!error || loading}
              onClick={onIntegrate}
              fullWidth
            >
              {t('integrationCard.connect')}
            </LoadingButton>
          )}
          {isIntegrated && !loading && onConfigure && (
            <Button fullWidth onClick={onConfigure} color='primary'>
              {t('integrationCard.configure')}
            </Button>
          )}
        </Box>
        {error && <Alert severity='error'>{error}</Alert>}
      </CardContent>
      {onDelete && showDeleteDialog && (
        <DeleteDialog
          open={showDeleteDialog}
          onDelete={onDelete}
          onClose={() => setShowDeleteDialog(false)}
          alertText={t('integrationCard.disconnectAlert')}
          title={t('integrationCard.disconnectTitle')}
          content={
            <Typography>{t('integrationCard.disconnectConfirm')}</Typography>
          }
          deleteButtonText={t('integrationCard.disconnectButton')}
        />
      )}
    </Card>
  );
};
export default IntegrationCard;
